import React from 'react';

// Time-travel control for the History tab: step between runs (◀ older, ▶ newer),
// jump via the dot timeline, and pick the "Compare to" baseline. `runs` is the API
// list (newest-first); `index` is the selected position in it.
const TYPE_LABEL = { ingest: 'Import', enrich: 'Enrich', enrichment: 'Enrichment' };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const labelOf = (run) => TYPE_LABEL[run.pipeline] || run.pipeline || 'Run';

export default function RunStepper({ runs = [], index = 0, baseline = 'prev', onPick, onBaseline }) {
  const current = runs[index];
  if (!current) return null;
  const atNewest = index <= 0;
  const atOldest = index >= runs.length - 1;

  return (
    <div className="flex items-center gap-3 flex-wrap bg-gray-100 border border-gray-200 rounded-xl px-3 py-2">
      <span className="text-xs text-gray-500 font-semibold">Viewing run</span>

      <div className="flex items-center gap-2">
        <button type="button" aria-label="older run" disabled={atOldest} onClick={() => onPick(index + 1)}
          className="w-7 h-7 grid place-items-center rounded-lg border border-gray-200 bg-white text-gray-700 disabled:opacity-40 disabled:cursor-default hover:bg-gray-50">◀</button>
        <span className="text-sm font-semibold text-gray-900 text-center min-w-[9rem]">
          {labelOf(current)}
          <span className="block text-[11px] font-medium text-gray-500">
            {fmtDate(current.completed_at)}{atNewest ? ' · latest' : ''}
          </span>
        </span>
        <button type="button" aria-label="newer run" disabled={atNewest} onClick={() => onPick(index - 1)}
          className="w-7 h-7 grid place-items-center rounded-lg border border-gray-200 bg-white text-gray-700 disabled:opacity-40 disabled:cursor-default hover:bg-gray-50">▶</button>
      </div>

      <div className="flex items-center gap-1.5">
        {runs.map((run, i) => i).reverse().map((i) => {
          const run = runs[i];
          const isCurrent = i === index;
          return (
            <button key={run.run_id} type="button" aria-current={isCurrent}
              aria-label={`go to ${labelOf(run)} · ${fmtDate(run.completed_at)}`}
              onClick={() => onPick(i)}
              className={`w-2.5 h-2.5 rounded-full p-0 ${isCurrent ? 'bg-[#00b39e] ring-2 ring-[#00b39e]/30' : 'bg-gray-300 hover:bg-gray-400'}`} />
          );
        })}
      </div>

      <div className="flex gap-0.5 ml-auto bg-white border border-gray-200 rounded-full p-0.5">
        {[['prev', 'vs previous run'], ['import', 'since ATS import']].map(([mode, label]) => (
          <button key={mode} type="button" onClick={() => onBaseline(mode)} aria-pressed={baseline === mode}
            className={`text-[11.5px] px-3 py-1 rounded-full ${baseline === mode ? 'bg-gray-100 text-gray-900 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
